export type Capability =
  | "capability:authenticated"
  | "capability:catalog_access"
  | "capability:ops_dashboard"
  | "capability:customer_portal"
  | "capability:analytics_dashboard"
  | "capability:partnerships_console"
  | "capability:admin_console"
  | "capability:policy_audit_read";

const CAPABILITY_INHERITANCE: Record<Capability, Capability[]> = {
  "capability:authenticated": [],
  "capability:catalog_access": ["capability:authenticated"],
  "capability:ops_dashboard": ["capability:catalog_access"],
  "capability:customer_portal": ["capability:catalog_access"],
  "capability:analytics_dashboard": ["capability:ops_dashboard"],
  "capability:partnerships_console": ["capability:analytics_dashboard"],
  "capability:admin_console": ["capability:partnerships_console"],
  "capability:policy_audit_read": ["capability:admin_console"],
};

const ROLE_CAPABILITY_GRANTS: Record<string, Capability[]> = {
  admin: ["capability:admin_console", "capability:policy_audit_read"],
  staff: ["capability:ops_dashboard"],
  customer: ["capability:customer_portal"],
  farmer: ["capability:ops_dashboard"],
  distributor: ["capability:ops_dashboard"],
  retailer: ["capability:ops_dashboard"],
};

const expandCapability = (capability: Capability, resolved: Set<Capability>, visited: Set<Capability>) => {
  if (visited.has(capability)) return;
  visited.add(capability);
  resolved.add(capability);

  for (const parent of CAPABILITY_INHERITANCE[capability] || []) {
    expandCapability(parent, resolved, visited);
  }
};

export const resolveCapabilities = (grants: string[] = []): string[] => {
  const resolved = new Set<Capability>();
  for (const rawCapability of grants) {
    const capability = rawCapability as Capability;
    if (!CAPABILITY_INHERITANCE[capability]) continue;
    expandCapability(capability, resolved, new Set<Capability>());
  }
  return Array.from(resolved).sort();
};

export const capabilitiesForRole = (role?: string): string[] => {
  const grants = ROLE_CAPABILITY_GRANTS[(role || "").toLowerCase()] || ["capability:authenticated"];
  return resolveCapabilities(grants);
};

export const hasCapability = (capabilities: string[] | undefined, requiredCapability: string): boolean => {
  if (!requiredCapability) return true;
  const effective = new Set(resolveCapabilities(capabilities || []));
  return effective.has(requiredCapability as Capability);
};
